import React, { useState, useEffect } from "react";
import { supabase } from "../supabase";

/**
 * OnboardingMVP - Simplified onboarding for service providers
 *
 * Phase 1: Essential Profile (minimum to receive inquiries)
 * - Step 1: Service Type (ultra-fast first step)
 * - Step 2: About You (name, DOB, location, photo) - Easy psychological win
 * - Step 3: Service Details (title, description)
 * - Step 4: Church Information (church name, denomination)
 * - Step 5: Basic Availability
 * - Step 6: Pricing Approach
 */

export default function OnboardingMVP() {
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  const [formData, setFormData] = useState({
    // Step 1: Service Type
    serviceType: [], // Array: "speaker", "musician", "other"
    otherServiceType: "", // Text input when "other" is selected

    // Step 2: About You (Personal Info)
    fullName: "",
    dateOfBirth: "",
    cityTown: "",
    profilePictureUrl: "",

    // Step 3: Service Details
    serviceTitle: "",
    serviceDescription: "", // Optional

    // Step 4: Church Information
    churchName: "",
    denomination: "",

    // Step 5: Basic Availability
    generalAvailability: [], // Array of: "weekdays", "weekends", "evenings", "weekday_evenings", "sunday_services", "very_flexible"
    minAdvanceNotice: "", // "1_week", "2_weeks", "1_month", "2_months", "flexible"

    // Travel fields (defaults - not collected in simplified onboarding)
    travelMethod: "car", // Default to car
    maxTravelDistance: 50, // Default 50 miles

    // Step 6: Pricing Approach
    acceptsHonorarium: "", // "yes" or "no"
    needsExpensesCovered: "", // "yes" or "no"
  });

  const totalSteps = 6;

  // Load existing profile data on mount
  useEffect(() => {
    loadExistingProfile();
  }, []);

  const loadExistingProfile = async () => {
    try {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError || !user) {
        console.error("Error getting user:", userError);
        setIsLoading(false);
        return;
      }

      // Fetch existing profile
      const { data: profile, error: profileError } = await supabase
        .from("user_profiles")
        .select("*")
        .eq("user_id", user.id)
        .single();

      if (profileError) {
        console.error("Error fetching profile:", profileError);
        setIsLoading(false);
        return;
      }

      if (profile) {
        // Map database fields to form state
        const loadedData = {
          // Step 1: Service Type
          serviceType: profile.service_types?.map(type => type.toLowerCase()) || [],
          otherServiceType: profile.other_service_type || "",

          // Step 2: About You (Personal info)
          fullName: profile.full_name || "",
          dateOfBirth: profile.date_of_birth || "",
          cityTown: profile.location || "",
          profilePictureUrl: profile.profile_picture_url || "",

          // Step 3: Service Details
          serviceTitle: profile.bio1 || "",
          serviceDescription: profile.service_description || "",

          // Step 4: Church Information
          churchName: profile.church_name || "",
          denomination: profile.denomination || "",

          // Step 5: Basic Availability
          generalAvailability: profile.available_hours?.availability_type
            ? profile.available_hours.availability_type.split(", ")
            : [],
          minAdvanceNotice: getDaysToNoticeKey(
            profile.available_hours?.min_advance_notice_days
          ),

          // Travel fields (set defaults, not collected in simplified onboarding)
          travelMethod: profile.travel_method?.[0] || "car",
          maxTravelDistance: profile.max_travel_distance || 50,

          // Step 6: Pricing
          acceptsHonorarium: profile.accepts_honorarium || "",
          needsExpensesCovered: profile.needs_expenses_covered || "",
        };

        setFormData(loadedData);

        // Set image preview if profile picture exists
        if (profile.profile_picture_url) {
          setImagePreview(profile.profile_picture_url);
        }
      }

      setIsLoading(false);
    } catch (error) {
      console.error("Error loading profile:", error);
      setIsLoading(false);
    }
  };

  // Helper function to convert days to notice key
  const getDaysToNoticeKey = (days) => {
    if (!days) return "";
    const mapping = {
      0: "flexible",
      7: "1_week",
      14: "2_weeks",
      30: "1_month",
      60: "2_months",
    };
    return mapping[days] || "flexible";
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle checkbox changes for arrays
  const handleCheckboxChange = (field, value) => {
    setFormData((prev) => {
      const currentArray = prev[field] || [];
      const newArray = currentArray.includes(value)
        ? currentArray.filter((item) => item !== value)
        : [...currentArray, value];

      // Clear otherServiceType if "other" is unchecked
      const updates = {
        ...prev,
        [field]: newArray,
      };

      if (field === "serviceType" && value === "other" && !newArray.includes("other")) {
        updates.otherServiceType = "";
      }

      return updates;
    });

    // Clear error for the field when user makes a change
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    // Clear otherServiceType error if "other" is unchecked
    if (field === "serviceType" && value === "other" && errors.otherServiceType) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.otherServiceType;
        return newErrors;
      });
    }
  };

  // Handle image upload
  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("Image size must be less than 5MB");
      return;
    }

    try {
      setUploadingImage(true);

      // Get current user
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        alert("You must be logged in to upload images");
        setUploadingImage(false);
        return;
      }

      // Create unique filename
      const fileExt = file.name.split(".").pop();
      const fileName = `${user.id}-${Date.now()}.${fileExt}`;
      const filePath = `profile-pictures/${fileName}`;

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from("profile-pictures")
        .upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (error) throw error;

      // Get public URL
      const {
        data: { publicUrl },
      } = supabase.storage.from("profile-pictures").getPublicUrl(filePath);

      // Update form data with image URL
      setFormData((prev) => ({
        ...prev,
        profilePictureUrl: publicUrl,
      }));

      // Set preview
      setImagePreview(publicUrl);

      setUploadingImage(false);
    } catch (error) {
      console.error("Error uploading image:", error);
      alert("Failed to upload image. Please try again.");
      setUploadingImage(false);
    }
  };

  // Validate current step
  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        if (formData.serviceType.length === 0) {
          newErrors.serviceType = "Please select at least one service type";
        }
        if (formData.serviceType.includes("other") && !formData.otherServiceType.trim()) {
          newErrors.otherServiceType = "Please specify your service type";
        }
        break;

      case 2:
        if (!formData.fullName.trim()) {
          newErrors.fullName = "Name is required";
        }
        if (!formData.cityTown.trim()) {
          newErrors.cityTown = "City/Town is required";
        }
        break;

      case 3:
        if (!formData.serviceTitle.trim()) {
          newErrors.serviceTitle = "Service title is required";
        }
        break;

      case 4:
        // Church information is optional, no validation needed
        break;

      case 5:
        if (formData.generalAvailability.length === 0) {
          newErrors.generalAvailability = "Please select at least one availability option";
        }
        if (!formData.minAdvanceNotice) {
          newErrors.minAdvanceNotice = "Please select minimum advance notice";
        }
        break;

      case 6:
        if (!formData.acceptsHonorarium) {
          newErrors.acceptsHonorarium = "Please indicate if you accept honorarium";
        }
        if (!formData.needsExpensesCovered) {
          newErrors.needsExpensesCovered = "Please indicate if you need expenses covered";
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Navigation handlers
  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
    setErrors({});
  };

  // Save and exit handler
  const handleSaveAndExit = async () => {
    setIsSaving(true);
    try {
      // Get current user
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError || !user) {
        alert("You must be logged in to save");
        setIsSaving(false);
        return;
      }

      // Map form data to database fields
      const profileData = {
        full_name: formData.fullName || null,
        date_of_birth: formData.dateOfBirth || null,
        church_name: formData.churchName || null,
        denomination: formData.denomination || null,
        profile_picture_url: formData.profilePictureUrl || null,
        location: formData.cityTown || null,
        bio1: formData.serviceTitle || null,
        service_description: formData.serviceDescription || null,
        service_types: formData.serviceType.length > 0
          ? formData.serviceType.map(type => type.charAt(0).toUpperCase() + type.slice(1))
          : null,
        other_service_type: formData.otherServiceType || null,
        available_hours: {
          availability_type: formData.generalAvailability.join(", "),
          min_advance_notice_days: getAdvanceNoticeDays(formData.minAdvanceNotice),
        },
        travel_method: formData.travelMethod ? [formData.travelMethod] : null,
        max_travel_distance: formData.maxTravelDistance || null,
        travel_distance_unit: "miles",
        accepts_honorarium: formData.acceptsHonorarium || null,
        needs_expenses_covered: formData.needsExpensesCovered || null,
        updated_at: new Date().toISOString(),
      };

      // Save to database
      const { error: profileError } = await supabase
        .from("user_profiles")
        .update(profileData)
        .eq("user_id", user.id);

      if (profileError) throw profileError;

      alert("Progress saved! You can continue your profile later.");
      console.log("Saved progress:", formData);
      // Redirect to dashboard or explore
      window.location.href = "/explore";
    } catch (error) {
      console.error("Error saving progress:", error);
      alert("There was an error saving your progress. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  // Submit handler
  const handleSubmit = async () => {
    if (!validateStep(currentStep)) {
      return;
    }

    setIsSaving(true);

    try {
      // Get current user
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError || !user) {
        alert("You must be logged in to submit");
        setIsSaving(false);
        return;
      }

      // Map form data to database fields
      const profileData = {
        user_id: user.id,
        full_name: formData.fullName,
        date_of_birth: formData.dateOfBirth || null,
        church_name: formData.churchName || null,
        denomination: formData.denomination || null,
        profile_picture_url: formData.profilePictureUrl || null,
        location: formData.cityTown,
        bio1: formData.serviceTitle,
        service_description: formData.serviceDescription || null,
        service_types: formData.serviceType.map(type => type.charAt(0).toUpperCase() + type.slice(1)),
        other_service_type: formData.otherServiceType || null,
        available_hours: {
          availability_type: formData.generalAvailability.join(", "),
          min_advance_notice_days: getAdvanceNoticeDays(formData.minAdvanceNotice),
        },
        travel_method: [formData.travelMethod],
        max_travel_distance: formData.maxTravelDistance,
        travel_distance_unit: "miles",
        accepts_honorarium: formData.acceptsHonorarium,
        needs_expenses_covered: formData.needsExpensesCovered,
        skill_provider: true,
        skill_active: true,
        skill_active_time: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      // Console log for now (will connect to Supabase later as per requirements)
      console.log("Form submitted with data:", formData);
      console.log("Mapped to database format:", profileData);

      // Save to database
      const { error: profileError } = await supabase
        .from("user_profiles")
        .upsert(profileData, {
          onConflict: "user_id",
        });

      if (profileError) throw profileError;

      // Show success message
      alert("Profile created! You can enhance it anytime to get more bookings");

      // Redirect to explore page
      window.location.href = "/explore";
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("There was an error creating your profile. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  // Helper function to convert advance notice to days
  const getAdvanceNoticeDays = (notice) => {
    const mapping = {
      "1_week": 7,
      "2_weeks": 14,
      "1_month": 30,
      "2_months": 60,
      "flexible": 0,
    };
    return mapping[notice] || 7;
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderStep1(); // Service Type
      case 2:
        return renderStep2(); // About You
      case 3:
        return renderStep4(); // Service Details
      case 4:
        return renderStep3(); // Church Information
      case 5:
        return renderStep5(); // Availability
      case 6:
        return renderStep6(); // Pricing
      default:
        return null;
    }
  };

  // Step 1: Service Type (Ultra-fast first step)
  const renderStep1 = () => (
    <div style={styles.stepContent}>
      <h2 style={styles.stepTitle}>Let's get started</h2>
      <p style={styles.stepDescription}>What type of service do you offer? (Select all that apply)</p>

      <div style={styles.formGroup}>
        <div style={styles.checkboxGroup}>
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={formData.serviceType.includes("speaker")}
              onChange={() => handleCheckboxChange("serviceType", "speaker")}
              style={styles.checkbox}
            />
            <span style={styles.checkboxText}>Speaker</span>
          </label>
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={formData.serviceType.includes("musician")}
              onChange={() => handleCheckboxChange("serviceType", "musician")}
              style={styles.checkbox}
            />
            <span style={styles.checkboxText}>Musician</span>
          </label>
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={formData.serviceType.includes("other")}
              onChange={() => handleCheckboxChange("serviceType", "other")}
              style={styles.checkbox}
            />
            <span style={styles.checkboxText}>Other</span>
          </label>
        </div>
        {errors.serviceType && (
          <div style={styles.error}>{errors.serviceType}</div>
        )}
      </div>

      {formData.serviceType.includes("other") && (
        <div style={styles.formGroup}>
          <label style={styles.label}>Please specify *</label>
          <input
            type="text"
            value={formData.otherServiceType}
            onChange={(e) => handleInputChange("otherServiceType", e.target.value)}
            placeholder="e.g. Worship Leader, Youth Worker, etc."
            style={styles.input}
          />
          {errors.otherServiceType && (
            <div style={styles.error}>{errors.otherServiceType}</div>
          )}
        </div>
      )}
    </div>
  );

  // Step 2: About You (Personal Info)
  const renderStep2 = () => (
    <div style={styles.stepContent}>
      <h2 style={styles.stepTitle}>About You</h2>
      <p style={styles.stepDescription}>Help churches know who you are</p>

      <div style={styles.formGroup}>
        <label style={styles.label}>Profile Picture (Optional)</label>
        <div style={styles.imageUploadContainer}>
          {imagePreview && (
            <div style={styles.imagePreviewContainer}>
              <img
                src={imagePreview}
                alt="Profile preview"
                style={styles.imagePreview}
              />
            </div>
          )}
          <input
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            style={styles.fileInput}
            id="profile-picture-upload"
          />
          <label htmlFor="profile-picture-upload" style={styles.fileInputLabel}>
            {uploadingImage ? "Uploading..." : imagePreview ? "Change Photo" : "Choose Photo"}
          </label>
        </div>
        <div style={styles.helperText}>
          A profile photo helps churches connect with you
        </div>
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Full Name *</label>
        <input
          type="text"
          value={formData.fullName}
          onChange={(e) => handleInputChange("fullName", e.target.value)}
          placeholder="e.g. John Smith"
          style={styles.input}
        />
        {errors.fullName && <div style={styles.error}>{errors.fullName}</div>}
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Date of Birth</label>
        <input
          type="date"
          value={formData.dateOfBirth}
          onChange={(e) => handleInputChange("dateOfBirth", e.target.value)}
          style={styles.input}
        />
        <div style={styles.helperText}>
          This won't be visible to other users - used for verification purposes only
        </div>
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Your City/Town *</label>
        <input
          type="text"
          value={formData.cityTown}
          onChange={(e) => handleInputChange("cityTown", e.target.value)}
          placeholder="e.g. London, Manchester, Birmingham"
          style={styles.input}
        />
        <div style={styles.helperText}>
          Where you're based (not your church location)
        </div>
        {errors.cityTown && <div style={styles.error}>{errors.cityTown}</div>}
      </div>
    </div>
  );

  // Step 3: Church Information
  const renderStep3 = () => (
    <div style={styles.stepContent}>
      <h2 style={styles.stepTitle}>Church Information</h2>
      <p style={styles.stepDescription}>Share your church affiliation</p>

      <div style={styles.formGroup}>
        <label style={styles.label}>Church Name</label>
        <input
          type="text"
          value={formData.churchName}
          onChange={(e) => handleInputChange("churchName", e.target.value)}
          placeholder="e.g. St. Mary's Church"
          style={styles.input}
        />
        <div style={styles.helperText}>
          The church you're affiliated with
        </div>
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Denomination</label>
        <input
          type="text"
          value={formData.denomination}
          onChange={(e) => handleInputChange("denomination", e.target.value)}
          placeholder="e.g. Baptist, Methodist, Catholic, Anglican"
          style={styles.input}
        />
        <div style={styles.helperText}>
          Your church denomination or tradition
        </div>
      </div>
    </div>
  );

  // Step 4: Service Details
  const renderStep4 = () => (
    <div style={styles.stepContent}>
      <h2 style={styles.stepTitle}>Describe Your Service</h2>
      <p style={styles.stepDescription}>Help churches understand what you offer</p>

      <div style={styles.formGroup}>
        <label style={styles.label}>Service Title *</label>
        <input
          type="text"
          value={formData.serviceTitle}
          onChange={(e) => handleInputChange("serviceTitle", e.target.value)}
          placeholder="e.g. Youth Speaker on Mental Health"
          style={styles.input}
        />
        <div style={styles.helperText}>
          This is how you'll appear in search results
        </div>
        {errors.serviceTitle && (
          <div style={styles.error}>{errors.serviceTitle}</div>
        )}
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Service Description</label>
        <textarea
          value={formData.serviceDescription}
          onChange={(e) => handleInputChange("serviceDescription", e.target.value)}
          placeholder="Brief overview of what you offer (e.g. topics you cover, your approach, experience)"
          style={{...styles.input, minHeight: "100px", resize: "vertical"}}
          rows={4}
        />
        <div style={styles.helperText}>
          Help churches understand what makes your service unique
        </div>
      </div>
    </div>
  );

  // Step 5: Basic Availability
  const renderStep5 = () => (
    <div style={styles.stepContent}>
      <h2 style={styles.stepTitle}>Your Availability</h2>
      <p style={styles.stepDescription}>When are you typically available?</p>

      <div style={styles.formGroup}>
        <label style={styles.label}>General Availability *</label>
        <div style={styles.checkboxGroup}>
          {[
            { value: "weekdays", label: "Weekdays" },
            { value: "weekends", label: "Weekends" },
            { value: "evenings", label: "Evenings" },
            { value: "weekday_evenings", label: "Weekday Evenings" },
            { value: "sunday_services", label: "Sunday Services" },
            { value: "very_flexible", label: "Very Flexible" },
          ].map((option) => (
            <label key={option.value} style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formData.generalAvailability.includes(option.value)}
                onChange={() =>
                  handleCheckboxChange("generalAvailability", option.value)
                }
                style={styles.checkbox}
              />
              <span style={styles.checkboxText}>{option.label}</span>
            </label>
          ))}
        </div>
        {errors.generalAvailability && (
          <div style={styles.error}>{errors.generalAvailability}</div>
        )}
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Minimum Advance Notice *</label>
        <select
          value={formData.minAdvanceNotice}
          onChange={(e) => handleInputChange("minAdvanceNotice", e.target.value)}
          style={styles.select}
        >
          <option value="">Select minimum notice...</option>
          <option value="1_week">1 week</option>
          <option value="2_weeks">2 weeks</option>
          <option value="1_month">1 month</option>
          <option value="2_months">2 months</option>
          <option value="flexible">Flexible</option>
        </select>
        {errors.minAdvanceNotice && (
          <div style={styles.error}>{errors.minAdvanceNotice}</div>
        )}
      </div>
    </div>
  );

  // Step 6: Pricing Approach
  const renderStep6 = () => (
    <div style={styles.stepContent}>
      <h2 style={styles.stepTitle}>Pricing</h2>
      <p style={styles.stepDescription}>Help churches understand your expectations</p>

      <div style={styles.formGroup}>
        <label style={styles.label}>Do you accept an honorarium? *</label>
        <div style={styles.radioGroup}>
          <label style={styles.radioLabel}>
            <input
              type="radio"
              name="acceptsHonorarium"
              value="yes"
              checked={formData.acceptsHonorarium === "yes"}
              onChange={(e) => handleInputChange("acceptsHonorarium", e.target.value)}
              style={styles.radio}
            />
            <span style={styles.radioText}>Yes</span>
          </label>
          <label style={styles.radioLabel}>
            <input
              type="radio"
              name="acceptsHonorarium"
              value="no"
              checked={formData.acceptsHonorarium === "no"}
              onChange={(e) => handleInputChange("acceptsHonorarium", e.target.value)}
              style={styles.radio}
            />
            <span style={styles.radioText}>No</span>
          </label>
        </div>
        {errors.acceptsHonorarium && (
          <div style={styles.error}>{errors.acceptsHonorarium}</div>
        )}
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Do you need expenses covered? *</label>
        <div style={styles.radioGroup}>
          <label style={styles.radioLabel}>
            <input
              type="radio"
              name="needsExpensesCovered"
              value="yes"
              checked={formData.needsExpensesCovered === "yes"}
              onChange={(e) => handleInputChange("needsExpensesCovered", e.target.value)}
              style={styles.radio}
            />
            <span style={styles.radioText}>Yes</span>
          </label>
          <label style={styles.radioLabel}>
            <input
              type="radio"
              name="needsExpensesCovered"
              value="no"
              checked={formData.needsExpensesCovered === "no"}
              onChange={(e) => handleInputChange("needsExpensesCovered", e.target.value)}
              style={styles.radio}
            />
            <span style={styles.radioText}>No</span>
          </label>
        </div>
        <div style={styles.helperText}>
          e.g. travel, accommodation
        </div>
        {errors.needsExpensesCovered && (
          <div style={styles.error}>{errors.needsExpensesCovered}</div>
        )}
      </div>
    </div>
  );

  // Show loading screen while fetching existing data
  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <p style={{ fontSize: "15px", color: "#525252", textAlign: "center" }}>
            Loading your profile...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Progress Indicator */}
        <div style={styles.progressContainer}>
          <div style={styles.progressBar}>
            <div
              style={{
                ...styles.progressFill,
                width: `${(currentStep / totalSteps) * 100}%`,
              }}
            />
          </div>
          <div style={styles.progressText}>
            Step {currentStep} of {totalSteps}
          </div>
        </div>

        {/* Step Content */}
        {renderStepContent()}

        {/* Navigation Buttons */}
        <div style={styles.buttonContainer}>
          <div style={styles.leftButtons}>
            {currentStep > 1 && (
              <button onClick={handleBack} style={styles.backButton}>
                Back
              </button>
            )}
          </div>

          <div style={styles.rightButtons}>
            <button
              onClick={handleSaveAndExit}
              style={styles.saveButton}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save and continue later"}
            </button>

            {currentStep < totalSteps ? (
              <button onClick={handleNext} style={styles.nextButton}>
                Next
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                style={styles.submitButton}
                disabled={isSaving}
              >
                {isSaving ? "Creating Profile..." : "Complete Profile"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Styles
const styles = {
  container: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    backgroundColor: "#fafafa",
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "2rem",
  },
  card: {
    maxWidth: "700px",
    width: "100%",
    backgroundColor: "#fff",
    borderRadius: "12px",
    border: "1px solid #e5e5e5",
    padding: "3rem",
  },
  progressContainer: {
    marginBottom: "2rem",
  },
  progressBar: {
    width: "100%",
    height: "8px",
    backgroundColor: "#e5e5e5",
    borderRadius: "4px",
    overflow: "hidden",
    marginBottom: "0.5rem",
  },
  progressFill: {
    height: "100%",
    backgroundColor: "#1a1a1a",
    transition: "width 0.3s ease",
  },
  progressText: {
    fontSize: "14px",
    color: "#737373",
    textAlign: "center",
  },
  stepContent: {
    marginBottom: "2rem",
  },
  stepTitle: {
    fontSize: "28px",
    fontWeight: "600",
    color: "#1a1a1a",
    marginBottom: "0.5rem",
  },
  stepDescription: {
    fontSize: "16px",
    color: "#525252",
    marginBottom: "2rem",
  },
  formGroup: {
    marginBottom: "1.5rem",
  },
  label: {
    display: "block",
    fontSize: "15px",
    fontWeight: "500",
    color: "#1a1a1a",
    marginBottom: "0.5rem",
  },
  input: {
    width: "100%",
    padding: "12px",
    fontSize: "15px",
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    border: "1px solid #e5e5e5",
    borderRadius: "8px",
    boxSizing: "border-box",
    transition: "border-color 0.2s ease",
  },
  imageUploadContainer: {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
  },
  imagePreviewContainer: {
    width: "80px",
    height: "80px",
    borderRadius: "50%",
    overflow: "hidden",
    border: "2px solid #e5e5e5",
  },
  imagePreview: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  fileInput: {
    display: "none",
  },
  fileInputLabel: {
    display: "inline-block",
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: "500",
    color: "#1a1a1a",
    backgroundColor: "#fafafa",
    border: "1px solid #e5e5e5",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  select: {
    width: "100%",
    padding: "12px",
    fontSize: "15px",
    border: "1px solid #e5e5e5",
    borderRadius: "8px",
    boxSizing: "border-box",
    backgroundColor: "#fff",
    cursor: "pointer",
  },
  radioGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "0.75rem",
  },
  radioLabel: {
    display: "flex",
    alignItems: "center",
    padding: "12px",
    border: "1px solid #e5e5e5",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  radio: {
    marginRight: "10px",
    cursor: "pointer",
  },
  radioText: {
    fontSize: "15px",
    color: "#1a1a1a",
  },
  checkboxGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "0.75rem",
  },
  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    padding: "12px",
    border: "1px solid #e5e5e5",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  checkbox: {
    marginRight: "10px",
    cursor: "pointer",
  },
  checkboxText: {
    fontSize: "15px",
    color: "#1a1a1a",
  },
  sliderContainer: {
    padding: "1rem 0",
  },
  slider: {
    width: "100%",
    height: "8px",
    borderRadius: "4px",
    outline: "none",
    cursor: "pointer",
  },
  sliderValue: {
    textAlign: "center",
    fontSize: "20px",
    fontWeight: "600",
    color: "#1a1a1a",
    marginTop: "1rem",
  },
  helperText: {
    fontSize: "13px",
    color: "#737373",
    marginTop: "0.25rem",
  },
  error: {
    fontSize: "13px",
    color: "#dc2626",
    marginTop: "0.25rem",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "2rem",
    paddingTop: "2rem",
    borderTop: "1px solid #e5e5e5",
  },
  leftButtons: {
    display: "flex",
    gap: "1rem",
  },
  rightButtons: {
    display: "flex",
    gap: "1rem",
    alignItems: "center",
  },
  backButton: {
    backgroundColor: "transparent",
    color: "#525252",
    padding: "12px 24px",
    fontSize: "15px",
    fontWeight: "500",
    border: "1px solid #e5e5e5",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  saveButton: {
    backgroundColor: "transparent",
    color: "#525252",
    padding: "12px 20px",
    fontSize: "14px",
    fontWeight: "500",
    border: "1px solid #e5e5e5",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  nextButton: {
    backgroundColor: "#1a1a1a",
    color: "#fff",
    padding: "12px 32px",
    fontSize: "15px",
    fontWeight: "600",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  submitButton: {
    backgroundColor: "#1a1a1a",
    color: "#fff",
    padding: "12px 32px",
    fontSize: "15px",
    fontWeight: "600",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
};
